import React from "react";
import styled from "styled-components";
import { Container, Section, Eyebrow } from "../components/ui/Container";
import { LinkButton } from "../components/ui/Button";
import { colors, fonts, media, radius } from "../theme";

export const TwoSides = () => (
  <Section>
    <Container>
      <Head>
        <Eyebrow>Two sides, one marketplace</Eyebrow>
        <h2>Borrow what you need.<br /><Subtle>Invest where it matters.</Subtle></h2>
        <p>
          Everpesa connects investors directly to vetted, growing small businesses.
          Capital flows where banks rarely go, at a fraction of the cost of informal lenders.
        </p>
      </Head>

      <Grid>
        <Card $accent="brand">
          <Tag>For Small Businesses</Tag>
          <h3>Working capital in days, not weeks. No land titles.</h3>
          <ul>
            <li><Check />Apply from a mobile phone in minutes</li>
            <li><Check />Scored on mobile money and stock, not collateral</li>
            <li><Check />Tenors of 30 to 90 days, sized to your sales cycle</li>
            <li><Check />Build a verifiable digital credit history</li>
            <li><Check />Designed for low literacy and rural users</li>
          </ul>
          <LinkButton variant="primary" to="/borrow">Apply for a loan</LinkButton>
        </Card>

        <Card $accent="gold">
          <Tag $gold>For Investors</Tag>
          <h3>Earn competitive returns funding real businesses you can see.</h3>
          <ul>
            <li><Check $gold />Invest from UGX 10,000 per unit</li>
            <li><Check $gold />Typical returns of up to 7.6% per cycle</li>
            <li><Check $gold />Principal protection on every loan</li>
            <li><Check $gold />Real time portfolio tracking</li>
            <li><Check $gold />Transparent risk ratings on every listing</li>
          </ul>
          <LinkButton variant="gold" to="/invest">Start investing</LinkButton>
        </Card>
      </Grid>
    </Container>
  </Section>
);

const Head = styled.div`
  max-width: 720px;
  margin: 0 auto 64px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 18px;
  align-items: center;

  h2 { font-family: ${fonts.display}; }
  p { font-size: 17px; color: ${colors.inkMuted}; }
  ${media.sm} { margin-bottom: 40px; }
`;

const Subtle = styled.span`
  background: linear-gradient(90deg, ${colors.brand}, ${colors.gold});
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px;
  ${media.md} { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: white;
  border: 1px solid ${colors.line};
  border-radius: ${radius.xl};
  padding: 40px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 18px;
  transition: transform 280ms ease, box-shadow 280ms ease, border-color 280ms ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 24px 48px -12px rgba(10,15,31,0.15);
    border-color: ${(p) => (p.$accent === "gold" ? colors.gold : colors.brand)};
  }

  h3 {
    font-size: clamp(1.5rem, 2.4vw, 1.9rem);
    color: ${colors.ink};
    line-height: 1.15;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 8px 0 8px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  li {
    display: flex;
    align-items: center;
    gap: 12px;
    color: ${colors.inkMuted};
    font-size: 15px;
  }

  a { align-self: flex-start; margin-top: 8px; }

  ${media.sm} { padding: 28px 22px; }
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.05em;
  background: ${(p) => (p.$gold ? colors.goldSoft : colors.brandSoft)};
  color: ${(p) => (p.$gold ? "#9A6700" : colors.brand)};
`;

const Check = styled.span`
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  border-radius: 50%;
  background: ${(p) => (p.$gold ? colors.goldSoft : colors.brandSoft)};
  color: ${(p) => (p.$gold ? "#9A6700" : colors.brand)};
  position: relative;
  &::after {
    content: "✓";
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
  }
`;
